'use client';
import type { Assign } from '@ark-ui/react';
import { DatePicker } from '@ark-ui/react';
import {
  type DatePickerVariantProps,
  datePicker,
} from '@terra/ui-styled-system';
import type { ComponentProps, HTMLStyledProps } from '@terra/ui-styled-system';
import { createStyleContext } from './utils/create-style-context';

const { withProvider, withContext } = createStyleContext(datePicker);

export type RootProviderProps = ComponentProps<typeof RootProvider>;
export const RootProvider = withProvider<
  HTMLDivElement,
  Assign<
    Assign<HTMLStyledProps<'div'>, DatePicker.RootProviderProps>,
    DatePickerVariantProps
  >
>(DatePicker.RootProvider, 'root');

export type RootProps = ComponentProps<typeof Root>;
export const Root = withProvider<
  HTMLDivElement,
  Assign<
    Assign<HTMLStyledProps<'div'>, DatePicker.RootProps>,
    DatePickerVariantProps
  >
>(DatePicker.Root, 'root');

export const ClearTrigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, DatePicker.ClearTriggerProps>
>(DatePicker.ClearTrigger, 'clearTrigger');

export const Content = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, DatePicker.ContentProps>
>(DatePicker.Content, 'content');

export const Control = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, DatePicker.ControlProps>
>(DatePicker.Control, 'control');

export const Input = withContext<
  HTMLInputElement,
  Assign<HTMLStyledProps<'input'>, DatePicker.InputProps>
>(DatePicker.Input, 'input');

export const Label = withContext<
  HTMLLabelElement,
  Assign<HTMLStyledProps<'label'>, DatePicker.LabelProps>
>(DatePicker.Label, 'label');

export const MonthSelect = withContext<
  HTMLSelectElement,
  Assign<HTMLStyledProps<'select'>, DatePicker.MonthSelectProps>
>(DatePicker.MonthSelect, 'monthSelect');

export const NextTrigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, DatePicker.NextTriggerProps>
>(DatePicker.NextTrigger, 'nextTrigger');

export const Positioner = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, DatePicker.PositionerProps>
>(DatePicker.Positioner, 'positioner');

export const PresetTrigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, DatePicker.PresetTriggerProps>
>(DatePicker.PresetTrigger, 'presetTrigger');

export const PrevTrigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, DatePicker.PrevTriggerProps>
>(DatePicker.PrevTrigger, 'prevTrigger');

export const RangeText = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, DatePicker.RangeTextProps>
>(DatePicker.RangeText, 'rangeText');

export const TableBody = withContext<
  HTMLTableSectionElement,
  Assign<HTMLStyledProps<'tbody'>, DatePicker.TableBodyProps>
>(DatePicker.TableBody, 'tableBody');

export const TableCell = withContext<
  HTMLTableCellElement,
  Assign<HTMLStyledProps<'td'>, DatePicker.TableCellProps>
>(DatePicker.TableCell, 'tableCell');

export const TableCellTrigger = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, DatePicker.TableCellTriggerProps>
>(DatePicker.TableCellTrigger, 'tableCellTrigger');

export const TableHead = withContext<
  HTMLTableSectionElement,
  Assign<HTMLStyledProps<'thead'>, DatePicker.TableHeadProps>
>(DatePicker.TableHead, 'tableHead');

export const TableHeader = withContext<
  HTMLTableCellElement,
  Assign<HTMLStyledProps<'th'>, DatePicker.TableHeaderProps>
>(DatePicker.TableHeader, 'tableHeader');

export const Table = withContext<
  HTMLTableElement,
  Assign<HTMLStyledProps<'table'>, DatePicker.TableProps>
>(DatePicker.Table, 'table');

export const TableRow = withContext<
  HTMLTableRowElement,
  Assign<HTMLStyledProps<'tr'>, DatePicker.TableRowProps>
>(DatePicker.TableRow, 'tableRow');

export const Trigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, DatePicker.TriggerProps>
>(DatePicker.Trigger, 'trigger');

export const ViewControl = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, DatePicker.ViewControlProps>
>(DatePicker.ViewControl, 'viewControl');

export const View = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, DatePicker.ViewProps>
>(DatePicker.View, 'view');

export const ViewTrigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, DatePicker.ViewTriggerProps>
>(DatePicker.ViewTrigger, 'viewTrigger');

export const YearSelect = withContext<
  HTMLSelectElement,
  Assign<HTMLStyledProps<'select'>, DatePicker.YearSelectProps>
>(DatePicker.YearSelect, 'yearSelect');

export { DatePickerContext as Context } from '@ark-ui/react';
